import { X } from "@phosphor-icons/react";

const HIDE_KEY = "bgw_hidden_layouts";
const readHidden = () => {
  try {
    return JSON.parse(localStorage.getItem(HIDE_KEY) || "[]");
  } catch (err) {
    return [];
  }
};

// Visual tiles for the countertop layout presets (Rectangle, L, U, Island, Peninsula, angled corner).
export const LayoutPresets = ({
  layouts,
  shapes,
  onPick,
  size = "md",
  testPrefix = "add-shape",
  className = "",
  hidden = [],
  onHide,
  onRestore,
  onDeleteCustom,
}) => {
  const big = size === "lg";
  const shown = layouts.filter((L) => !hidden.includes(L.key));
  return (
    <div className={className}>
    <div
      className={`grid gap-3 ${big ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" : "grid-cols-2"}`}
      data-testid="designer-layouts"
    >
      {shown.map((L) => {
        const pts = L.points || shapes[L.key]();
        const w = Math.max(...pts.map((p) => p[0]));
        const h = Math.max(...pts.map((p) => p[1]));
        const slug = L.key.toLowerCase().replace(/ /g, "-");
        const remove = L.custom ? (onDeleteCustom ? () => onDeleteCustom(L.id) : null) : onHide ? () => onHide(L.key) : null;
        return (
          <div key={L.key} className="relative">
          {remove && (
            <button
              type="button"
              data-testid={`${testPrefix}-remove-${slug}`}
              title={L.custom ? "Delete this custom layout" : "Remove this layout"}
              onClick={(e) => {
                e.stopPropagation();
                remove();
              }}
              className="absolute right-1.5 top-1.5 z-10 rounded-md p-1 text-zinc-600 transition-colors duration-150 hover:bg-zinc-800 hover:text-red-400"
            >
              <X size={11} weight="bold" />
            </button>
          )}
          <button
            type="button"
            data-testid={`${testPrefix}-${slug}`}
            onClick={() => onPick(L.key)}
            className="group w-full rounded-xl border border-zinc-800 bg-zinc-950/80 p-3 text-left transition-all duration-150 hover:-translate-y-0.5 hover:border-indigo-400/70 hover:bg-indigo-500/5 hover:shadow-[0_8px_24px_-12px_rgba(99,102,241,0.6)]"
          >
            <svg
              viewBox={`-4 -4 ${w + 8} ${h + 8}`}
              className={`w-full ${big ? "h-20" : "h-14"}`}
              preserveAspectRatio="xMidYMid meet"
            >
              <polygon
                points={pts.map(([x, y]) => `${x},${y}`).join(" ")}
                fill="rgba(99,102,241,0.18)"
                stroke="#a5b4fc"
                strokeWidth={Math.max(w, h) / 40}
                strokeLinejoin="round"
                className="transition-colors duration-150 group-hover:fill-[rgba(99,102,241,0.32)]"
              />
            </svg>
            <div className={`mt-2 font-semibold text-zinc-200 group-hover:text-white ${big ? "text-xs" : "text-[11px]"}`}>
              {L.label}
              {L.custom && <span className="ml-1 rounded bg-indigo-500/20 px-1 text-[8px] uppercase text-indigo-200">mine</span>}
            </div>
            <div className="font-mono-data mt-0.5 text-[9px] text-zinc-500">{L.note}</div>
          </button>
          </div>
        );
      })}
    </div>
    {hidden.length > 0 && onRestore && (
      <button
        type="button"
        data-testid={`${testPrefix}-restore-hidden`}
        onClick={onRestore}
        className="mt-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 transition-colors duration-150 hover:text-indigo-200"
      >
        Restore {hidden.length} hidden layout{hidden.length > 1 ? "s" : ""}
      </button>
    )}
    </div>
  );
};

export const useHiddenLayouts = (useState) => {
  const [hidden, setHidden] = useState(readHidden);
  const persist = (next) => {
    setHidden(next);
    try {
      localStorage.setItem(HIDE_KEY, JSON.stringify(next));
    } catch (err) {
      /* ignore */
    }
  };
  return {
    hidden,
    onHide: (k) => persist([...hidden.filter((x) => x !== k), k]),
    onRestore: () => persist([]),
  };
};

export default LayoutPresets;
